import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import { Sun, Moon, Monitor, Braces, Clock, FileCode, Github, Wrench, BookOpen, HelpCircle } from 'lucide-react';
import JsonTabsView from './components/JsonTabsView';
import TimestampConverter from './components/TimestampConverter';
import GoStructToJson from './components/GoStructToJson';
import BlogList from './components/Blog/BlogList';
import BlogPost from './components/Blog/BlogPost';
import JsonHelp from './components/Help/JsonHelp';
import TimestampHelp from './components/Help/TimestampHelp';
import GoStructHelp from './components/Help/GoStructHelp';
import PrivacyPolicy from './components/Legal/PrivacyPolicy';
import CookiePolicy from './components/Legal/CookiePolicy';
import TermsOfService from './components/Legal/TermsOfService';
import About from './components/Legal/About';
import Contact from './components/Legal/Contact';
import SEOHead from './components/SEOHead';
import { LanguageProvider, useLanguage } from './i18n';
import { initGA, trackPageView } from './analytics/ga';
import DashboardLayout from './components/Layout/DashboardLayout';

const THEME_STORAGE_KEY = 'json-tool-theme';
const TOOL_STORAGE_KEY = 'current-tool';

/**
 * 工具列表配置
 */
const TOOLS = [
  {
    id: 'json',
    path: '/json',
    name: 'JSON 格式化',
    icon: Braces,
    color: 'bg-blue-500',
    theme: {
      primary: 'bg-blue-600 hover:bg-blue-700',
      light: 'bg-blue-50 dark:bg-blue-900/20',
      text: 'text-blue-600 dark:text-blue-400',
      border: 'border-blue-200 dark:border-blue-700',
      activeTab: 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300 border-indigo-200 dark:border-indigo-700',
      secondary: 'bg-indigo-600 hover:bg-indigo-700',
      secondaryLight: 'bg-indigo-50 dark:bg-indigo-900/20',
      secondaryText: 'text-indigo-600 dark:text-indigo-400',
    },
    description: 'JSON 解析、格式化和查看'
  },
  {
    id: 'timestamp',
    path: '/timestamp',
    name: '时间戳',
    icon: Clock,
    color: 'bg-orange-500',
    theme: {
      primary: 'bg-orange-600 hover:bg-orange-700',
      light: 'bg-orange-50 dark:bg-orange-900/20',
      text: 'text-orange-600 dark:text-orange-400',
      border: 'border-orange-200 dark:border-orange-700',
      gradient: 'from-orange-100 to-amber-100 dark:from-gray-800 dark:to-gray-700',
      secondary: 'bg-amber-500 hover:bg-amber-600',
      secondaryLight: 'bg-amber-50 dark:bg-amber-900/20',
      secondaryText: 'text-amber-600 dark:text-amber-400',
    },
    description: 'Unix 时间戳与日期时间转换'
  },
  {
    id: 'gostruct',
    path: '/gostruct',
    name: 'Go ↔ JSON',
    icon: FileCode,
    color: 'bg-emerald-500',
    theme: {
      primary: 'bg-emerald-600 hover:bg-emerald-700',
      light: 'bg-emerald-50 dark:bg-emerald-900/20',
      text: 'text-emerald-600 dark:text-emerald-400',
      border: 'border-emerald-200 dark:border-emerald-700',
      gradient: 'from-emerald-100 to-teal-100 dark:from-gray-800 dark:to-gray-700',
      secondary: 'bg-teal-600 hover:bg-teal-700',
      secondaryLight: 'bg-teal-50 dark:bg-teal-900/20',
      secondaryText: 'text-teal-600 dark:text-teal-400',
    },
    description: 'Go 结构体与 JSON 互转'
  }
];

/**
 * 根据路径获取当前工具 ID
 */
function getToolIdFromPath(pathname) {
  const tool = TOOLS.find(t => t.path === pathname);
  return tool ? tool.id : null;
}

/**
 * 主应用内容组件
 */
function AppContent() {
  const location = useLocation();
  const navigate = useNavigate();
  const { language, t } = useLanguage();

  // 根据 URL 确定当前工具，如果是根路径则使用 localStorage 记录的值
  const currentTool = getToolIdFromPath(location.pathname) ||
    localStorage.getItem(TOOL_STORAGE_KEY) ||
    'json';

  // 访问根路径时，重定向到上次使用的工具
  useEffect(() => {
    if (location.pathname === '/') {
      const savedTool = localStorage.getItem(TOOL_STORAGE_KEY) || 'json';
      const tool = TOOLS.find(t => t.id === savedTool);
      if (tool) {
        navigate(tool.path, { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  // 初始化 Google Analytics（仅执行一次）
  useEffect(() => {
    initGA();
  }, []);

  // 追踪路由变化
  useEffect(() => {
    // 获取页面标题
    const pageTitle = document.title;
    // 追踪页面浏览
    trackPageView(location.pathname, pageTitle);
  }, [location.pathname]);

  // 记录当前工具到 localStorage
  useEffect(() => {
    const toolId = getToolIdFromPath(location.pathname);
    if (toolId) {
      localStorage.setItem(TOOL_STORAGE_KEY, toolId);
    }
  }, [location.pathname]);


  return (
    <div className="flex flex-col min-h-screen transition-colors duration-200">
      {/* SEO Head */}
      <SEOHead page={currentTool} />


      {/* 工具内容区 */}
      <main className="flex-1">
        {/* SEO H1 标题 - 视觉隐藏但对搜索引擎可见 */}
        <h1 className="sr-only">
          {currentTool === 'json' && t('seo.json.title')}
          {currentTool === 'timestamp' && t('seo.timestamp.title')}
          {currentTool === 'gostruct' && t('seo.gostruct.title')}
        </h1>

        <div className={currentTool === 'json' ? '' : 'hidden'}>
          <JsonTabsView theme={TOOLS[0].theme} />
        </div>
        <div className={currentTool === 'timestamp' ? '' : 'hidden'}>
          <TimestampConverter theme={TOOLS[1].theme} />
        </div>
        <div className={currentTool === 'gostruct' ? '' : 'hidden'}>
          <GoStructToJson theme={TOOLS[2].theme} />
        </div>
      </main>

    </div>
  );
}

/**
 * 带布局的路由组件
 */
function AppWithLayout() {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return saved || 'system';
  });

  // 应用主题到 DOM
  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');

    if (theme === 'system') {
      const systemTheme = window.matchMedia('(prefers-color-scheme: dark)').matches
        ? 'dark'
        : 'light';
      root.classList.add(systemTheme);
    } else {
      root.classList.add(theme);
    }

    localStorage.setItem(THEME_STORAGE_KEY, theme);
  }, [theme]);

  return (
    <DashboardLayout theme={theme} setTheme={setTheme}>
      <Routes>
        {/* 博客路由 */}
        <Route path="/blog" element={<BlogList />} />
        <Route path="/blog/:slug" element={<BlogPost />} />

        {/* 帮助文档页面 */}
        <Route path="/help/json" element={<JsonHelp />} />
        <Route path="/help/timestamp" element={<TimestampHelp />} />
        <Route path="/help/go-struct" element={<GoStructHelp />} />

        {/* 法律和政策页面 */}
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/cookie-policy" element={<CookiePolicy />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/about" element={<About />} />
        <Route path="/contact" element={<Contact />} />

        {/* 工具页面路由 */}
        <Route path="/*" element={<AppContent />} />
      </Routes>
    </DashboardLayout>
  );
}

/**
 * 根组件 - 包装 Router 和 LanguageProvider
 */
function App() {
  return (
    <LanguageProvider>
      <BrowserRouter>
        <AppWithLayout />
      </BrowserRouter>
    </LanguageProvider>
  );
}

export default App;
